import React, { ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthProvider';

interface AuthGuardProps {
  children: ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { isAuthenticated, isLoading, login } = useAuthContext();
  const location = useLocation();
  const loginStartedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inCallback = params.has('code') && params.has('state');

    // Redirect to OIDC login immediately if not authenticated
    if (!isAuthenticated && !isLoading && !inCallback && !loginStartedRef.current) {
      loginStartedRef.current = true;
      login(); // Trigger OIDC login immediately - redirects to OIDC provider
      return;
    }

    if (isAuthenticated) {
      loginStartedRef.current = false;
    }
  }, [isAuthenticated, isLoading, login, location.search]);

  // Show loader while checking authentication status
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          textAlign: 'center'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '5px solid #f3f3f3',
            borderTop: '5px solid #0557D5',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p style={{
            color: '#666',
            fontSize: '16px',
            margin: 0
          }}>
            Loading...
          </p>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Show loader while redirecting to OIDC login
  if (!isAuthenticated) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          textAlign: 'center'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '5px solid #f3f3f3',
            borderTop: '5px solid #0557D5',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p style={{
            color: '#666',
            fontSize: '16px',
            margin: 0
          }}>
            Redirecting to login...
          </p>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return <>{children}</>;
};
